import { useState } from 'react';
import { X, Sparkles, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsletterPopup = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/newsletter/subscribe`, { email });
      if (response.data.already_subscribed) {
        toast.info("You're already on our list!");
      } else {
        setSubscribed(true);
        toast.success("Welcome to the Blush & Go family!");
      }
    } catch (error) {
      toast.error("Oops! Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="newsletter-popup"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="popup-enter bg-white w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
        {/* Image Side */}
        <div className="hidden md:block w-1/2 relative">
          <img
            src="https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600"
            alt="Beauty products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        </div>

        {/* Form Side */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-neutral-100 transition-colors"
            data-testid="newsletter-close-btn"
            aria-label="Close popup"
          >
            <X className="w-5 h-5" />
          </button>

          {!subscribed ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-champagne" />
                </div>
              </div>

              {/* Content */}
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
                  Unlock Your Glow
                </h2>
                <p className="text-neutral-600 leading-relaxed">
                  Join the Blush & Go community and be the <span className="font-semibold">FIRST</span> to discover:
                </p>
                <ul className="text-left text-sm text-neutral-600 mt-4 space-y-2 max-w-xs mx-auto">
                  <li className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-champagne flex-shrink-0" />
                    Exclusive deals & early access
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-champagne flex-shrink-0" />
                    Hot new beauty drops
                  </li>
                  <li className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-champagne flex-shrink-0" />
                    Pro tips from beauty experts
                  </li>
                </ul>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 border border-neutral-300 rounded-none px-4 text-center"
                  data-testid="newsletter-email-input"
                  required
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#1A1A1A] text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-bold"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    "YES, I'M IN!"
                  )}
                </Button>
              </form>

              <p className="text-xs text-neutral-400 text-center mt-4">
                No spam, ever. Unsubscribe anytime.
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl font-semibold mb-4">
                You're In!
              </h2>
              <p className="text-neutral-600 mb-6">
                Get ready for exclusive beauty finds delivered straight to your inbox.
              </p>
              <Button
                onClick={onClose}
                className="h-12 px-8 bg-[#1A1A1A] text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-bold"
                data-testid="newsletter-continue-btn"
              >
                START SHOPPING
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
